import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Loader2 } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { aiApi } from "@/lib/api";

/**
 * Cmd/Ctrl+K command palette.
 * - User types a natural-language command.
 * - Backend (Claude Sonnet 4.5) returns a single action JSON.
 * - We dispatch known actions; show "explain" / "unsupported" inline.
 *
 * The action dispatcher is provided by the caller (Schedule.jsx) so the palette
 * stays decoupled from the grid mutators.
 */
export default function CommandPalette({ open, onClose, projectId, onAction }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setValue("");
    setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    const cmd = value.trim();
    if (!cmd || busy || !projectId) return;
    setBusy(true);
    const turn = { command: cmd, response: null, t: Date.now() };
    setHistory((h) => [turn, ...h].slice(0, 8));
    setValue("");
    try {
      const action = await aiApi.command(projectId, cmd);
      setHistory((h) => h.map((t) => (t.t === turn.t ? { ...t, response: action } : t)));
      const handled = await onAction?.(action);
      if (handled === false && action.action !== "explain" && action.action !== "unsupported") {
        toast.error("Action not dispatched.");
      }
    } catch (e) {
      setHistory((h) => h.map((t) => (t.t === turn.t ? { ...t, response: { action: "unsupported", message: e.response?.data?.detail || "Request failed" } } : t)));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/70 backdrop-blur-sm pt-[10vh]"
      onClick={onClose}
      data-testid="command-palette-overlay"
    >
      <div
        className="w-[640px] max-w-[92vw] bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-palette"
      >
        <div className="flex items-center gap-2 p-3 border-b border-line-strong bg-bg-elevated">
          <Sparkles className="w-4 h-4 text-amber" />
          <div className="flex-1">
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">AI · CLAUDE SONNET 4.5</div>
            <div className="text-sm text-ink mt-0.5">Command palette</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="command-palette-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="p-3 border-b border-line">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submit(); }
              }}
              placeholder="e.g. push the truss build by 30m · lock lunch · what's drifting"
              className="flex-1 bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              disabled={busy}
              data-testid="command-palette-input"
            />
            <button
              onClick={submit}
              disabled={busy || !value.trim()}
              className="btn-primary px-3"
              data-testid="command-palette-submit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </button>
          </div>
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-2">
            Try: "delay X by 15m" · "lock the load-in" · "show critical only" · "add rooms: Stage C, Tent B" · "add tasks: PA tune (90m, high), Site walk"
          </div>
        </div>

        <div className="max-h-72 overflow-auto">
          {history.length === 0 && (
            <div className="p-4 mono text-2xs uppercase tracking-widest text-ink-mute">
              — no commands yet —
            </div>
          )}
          {history.map((turn) => (
            <div key={turn.t} className="px-3 py-2 border-b border-line-subtle last:border-b-0">
              <div className="text-xs text-ink">{turn.command}</div>
              {turn.response ? (
                <ActionLine action={turn.response} />
              ) : (
                <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-1">
                  thinking…
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionLine({ action }) {
  const a = action.action;
  if (a === "explain") {
    return <div className="text-2xs text-cyan mt-1 leading-relaxed">{action.message}</div>;
  }
  if (a === "unsupported") {
    return <div className="text-2xs text-red mt-1">{action.message || "Unsupported."}</div>;
  }
  if (a?.startsWith("bulk_add_") || a === "add_task_with_subtasks") {
    const n = a === "add_task_with_subtasks" ? (action.subtasks?.length || 0) : (action.items?.length || 0);
    const label = a === "add_task_with_subtasks" ? `Parent + ${n} subtask${n === 1 ? "" : "s"}` : `${n} item${n === 1 ? "" : "s"}`;
    return (
      <div className="mono text-2xs uppercase tracking-widest text-amber mt-1">
        → review {a.replace("bulk_add_", "").replace("_", " ")} ({label})
      </div>
    );
  }
  return (
    <div className="mono text-2xs uppercase tracking-widest text-amber mt-1">
      → {a.replace(/_/g, " ")} {extraDetail(action)}
    </div>
  );
}

function extraDetail(a) {
  if (a.delta_min != null) return `· ${a.delta_min > 0 ? "+" : ""}${a.delta_min}m`;
  if (a.status) return `· ${a.status}`;
  if (a.locked != null) return `· ${a.locked ? "locked" : "unlocked"}`;
  if (a.room_ids) return `· ${a.room_ids.length} room${a.room_ids.length === 1 ? "" : "s"}`;
  if (a.statuses) return `· ${a.statuses.join(", ")}`;
  return "";
}
